import { useEffect, useState, useRef } from "react";
import adminApi from "../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash, Pencil, Eye, EyeSlash, UploadSimple, X, FileText, VideoCamera, Article, Image as ImageIcon } from "@phosphor-icons/react";
import MDEditor from "@uiw/react-md-editor";

const EMPTY = {
  kind: "article",
  title: "",
  slug: "",
  excerpt: "",
  body_markdown: "",
  cover_image_url: "",
  video_provider: "youtube",
  video_url: "",
  video_file_id: "",
  tags: [],
  published: false,
};

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 Mo (sous la limite proxy 10 Mo)

export default function AdminCMS() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileRef = useRef(null);

  const reload = async () => {
    const r = await adminApi.get("/cms/items");
    setItems(r.data.items || []);
  };

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  const startNew = (kind) => {
    setForm({ ...EMPTY, kind });
    setEditingId(null);
    setTagsInput("");
    setShowForm(true);
  };

  const startEdit = (it) => {
    setForm({
      kind: it.kind,
      title: it.title || "",
      slug: it.slug || "",
      excerpt: it.excerpt || "",
      body_markdown: it.body_markdown || "",
      cover_image_url: it.cover_image_url || "",
      video_provider: it.video_provider || "youtube",
      video_url: it.video_url || "",
      video_file_id: it.video_file_id || "",
      tags: it.tags || [],
      published: !!it.published,
    });
    setTagsInput((it.tags || []).join(", "));
    setEditingId(it.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await adminApi.patch(`/cms/items/${editingId}`, payload);
        toast.success("Contenu mis à jour");
      } else {
        await adminApi.post("/cms/items", payload);
        toast.success("Contenu créé");
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      setTagsInput("");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce contenu ?")) return;
    await adminApi.delete(`/cms/items/${id}`);
    toast.success("Contenu supprimé");
    await reload();
  };

  const togglePublish = async (it) => {
    await adminApi.patch(`/cms/items/${it.id}`, {
      kind: it.kind,
      title: it.title,
      excerpt: it.excerpt,
      body_markdown: it.body_markdown,
      cover_image_url: it.cover_image_url,
      video_provider: it.video_provider,
      video_url: it.video_url,
      video_file_id: it.video_file_id,
      tags: it.tags || [],
      published: !it.published,
    });
    await reload();
  };

  // ---------- Chunked video upload ----------
  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 500 Mo)");
      return;
    }
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      setUploadProgress({ percent: 0, name: file.name });
      const init = await adminApi.post("/cms/uploads/init", {
        filename: file.name,
        size: file.size,
        mime: file.type || "video/mp4",
        total_chunks: totalChunks,
      });
      const uploadId = init.data.id;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("chunk_index", String(i));
        fd.append("chunk", blob, `chunk-${i}`);
        await adminApi.post(`/cms/uploads/${uploadId}/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUploadProgress({ percent: Math.round(((i + 1) / totalChunks) * 100), name: file.name });
      }
      const done = await adminApi.post(`/cms/uploads/${uploadId}/complete`);
      setForm((f) => ({ ...f, video_provider: "upload", video_url: done.data.url, video_file_id: uploadId }));
      toast.success("Vidéo téléversée");
      setUploadProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      console.error(err);
      toast.error("Échec du téléversement");
      setUploadProgress(null);
    }
  };

  // ---------- Cover image upload (one-shot) ----------
  const handleCoverImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 10 Mo)");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const r = await adminApi.post("/cms/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setForm((f) => ({ ...f, cover_image_url: fullUrl }));
      toast.success("Image de couverture téléversée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du téléversement");
    }
  };

  const kindIcon = (k) => {
    if (k === "video") return VideoCamera;
    if (k === "post") return FileText;
    return Article;
  };

  return (
    <div data-testid="admin-cms-page" data-color-mode="light">
      <div className="label-mono">§ 03 · CMS Page d'accueil</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-medium mt-2">Contenus publiés</h1>
      <p className="text-[var(--muted)] mt-3 max-w-2xl">
        Articles, posts courts et vidéos affichés sur la homepage et le glossaire public. Les contenus non publiés restent invisibles.
      </p>

      {/* Action buttons */}
      <div className="mt-8 flex flex-wrap gap-2">
        <button type="button" onClick={() => startNew("article")} className="border border-black hover:bg-black hover:text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2" data-testid="cms-new-article">
          <Plus size={14} weight="bold" /> Article
        </button>
        <button type="button" onClick={() => startNew("post")} className="border border-black hover:bg-black hover:text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2" data-testid="cms-new-post">
          <Plus size={14} weight="bold" /> Post court
        </button>
        <button type="button" onClick={() => startNew("video")} className="border border-black hover:bg-black hover:text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2" data-testid="cms-new-video">
          <Plus size={14} weight="bold" /> Vidéo
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={submit} className="mt-8 border-2 border-[var(--signal)] p-6 bg-[var(--paper)]" data-testid="cms-form">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl font-medium">
              {editingId ? "Modifier" : "Nouveau"} · {form.kind}
            </h2>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm underline flex items-center gap-1" data-testid="cms-form-close">
              <X size={14} /> Annuler
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <div className="label-mono mb-2">Titre</div>
              <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-black px-4 py-3" data-testid="cms-form-title" />
            </label>
            <label className="block">
              <div className="label-mono mb-2">Slug URL (facultatif, auto-généré)</div>
              <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="w-full border border-black px-4 py-3 font-mono text-xs" data-testid="cms-form-slug" />
            </label>
            <label className="block">
              <div className="label-mono mb-2">Image de couverture</div>
              <div className="flex flex-col gap-2">
                <input type="url" value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} className="w-full border border-black px-4 py-3 font-mono text-xs" placeholder="URL ou téléverser ↓" data-testid="cms-form-cover" />
                <label className="inline-flex items-center gap-2 border border-dashed border-black/40 px-3 py-2 cursor-pointer hover:bg-black/5 text-xs label-mono" data-testid="cms-form-cover-upload-label">
                  <ImageIcon size={14} /> Téléverser une image (max 10 Mo)
                  <input type="file" accept="image/*" onChange={handleCoverImage} className="hidden" data-testid="cms-form-cover-upload" />
                </label>
                {form.cover_image_url && (
                  <img src={form.cover_image_url} alt="aperçu" className="border border-black/20 max-h-32 object-cover" />
                )}
              </div>
            </label>
            <label className="block md:col-span-2">
              <div className="label-mono mb-2">Extrait / résumé (1-2 phrases)</div>
              <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full border border-black px-4 py-3" data-testid="cms-form-excerpt" />
            </label>
            {form.kind !== "video" && (
              <div className="block md:col-span-2" data-testid="cms-form-body-wrapper">
                <div className="label-mono mb-2">Corps (Markdown · éditeur WYSIWYG + preview)</div>
                <div className="border border-black bg-white" data-testid="cms-form-body">
                  <MDEditor
                    value={form.body_markdown || ""}
                    onChange={(v) => setForm({ ...form, body_markdown: v || "" })}
                    height={420}
                    preview="live"
                  />
                </div>
              </div>
            )}

            {form.kind === "video" && (
              <>
                <label className="block">
                  <div className="label-mono mb-2">Type de vidéo</div>
                  <select value={form.video_provider} onChange={(e) => setForm({ ...form, video_provider: e.target.value })} className="w-full border border-black px-4 py-3 bg-white" data-testid="cms-form-vprov">
                    <option value="youtube">YouTube (embed)</option>
                    <option value="vimeo">Vimeo (embed)</option>
                    <option value="upload">Upload local</option>
                  </select>
                </label>
                {form.video_provider !== "upload" ? (
                  <label className="block">
                    <div className="label-mono mb-2">URL de la vidéo</div>
                    <input type="url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="w-full border border-black px-4 py-3 font-mono text-xs" placeholder="https://www.youtube.com/watch?v=..." data-testid="cms-form-vurl" />
                  </label>
                ) : (
                  <label className="block">
                    <div className="label-mono mb-2">Fichier vidéo (max 500 Mo)</div>
                    <input ref={fileRef} type="file" accept="video/*" onChange={handleVideoFile} className="w-full border border-black px-4 py-3" data-testid="cms-form-vfile" />
                    {uploadProgress && (
                      <div className="mt-2 text-xs">
                        <div className="font-mono">{uploadProgress.name} · {uploadProgress.percent}%</div>
                        <div className="h-1 bg-black/10 mt-1">
                          <div className="h-full bg-[var(--signal)]" style={{ width: `${uploadProgress.percent}%` }} />
                        </div>
                      </div>
                    )}
                    {form.video_url && (
                      <div className="mt-2 text-xs text-[var(--muted)] font-mono break-all">✓ {form.video_url}</div>
                    )}
                  </label>
                )}
              </>
            )}

            <label className="block md:col-span-2">
              <div className="label-mono mb-2">Tags (séparés par virgule)</div>
              <input type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="w-full border border-black px-4 py-3" placeholder="dette, UEMOA, vitaliste" data-testid="cms-form-tags" />
            </label>
            <label className="flex items-center gap-2 md:col-span-2">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="cms-form-published" />
              <span className="text-sm">Publier immédiatement (visible publiquement)</span>
            </label>
          </div>
          <button type="submit" className="mt-6 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-3 label-mono flex items-center gap-2" data-testid="cms-form-submit">
            <UploadSimple size={14} weight="bold" /> {editingId ? "Enregistrer" : "Créer le contenu"}
          </button>
        </form>
      )}

      {/* List */}
      <div className="mt-10">
        {items.length === 0 ? (
          <div className="border border-dashed border-black/30 p-10 text-center text-[var(--muted)] italic" data-testid="cms-empty">
            Aucun contenu créé pour l'instant.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {items.map((it) => {
              const Icon = kindIcon(it.kind);
              return (
                <div key={it.id} className={`border-2 p-4 ${it.published ? "border-black" : "border-black/30 bg-[var(--paper)] opacity-80"}`} data-testid={`cms-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon size={16} className="text-[var(--signal)]" />
                        <span className="label-mono text-[var(--muted)]">{it.kind}</span>
                        {it.published ? (
                          <span className="label-mono bg-[var(--ink)] text-[var(--paper)] px-2 py-0.5 text-[10px]">PUBLIÉ</span>
                        ) : (
                          <span className="label-mono border border-black/40 px-2 py-0.5 text-[10px]">BROUILLON</span>
                        )}
                      </div>
                      <h3 className="font-serif text-xl font-medium mt-2 break-words">{it.title}</h3>
                      <div className="font-mono text-xs text-[var(--muted)] mt-1">/{it.slug} · {it.views || 0} vue(s)</div>
                      {it.excerpt && <p className="text-sm mt-2 text-[var(--muted)] line-clamp-2">{it.excerpt}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => togglePublish(it)} className="border border-black hover:bg-black hover:text-[var(--paper)] px-3 py-1.5 label-mono text-xs flex items-center gap-1" data-testid={`cms-toggle-${it.id}`}>
                      {it.published ? <><EyeSlash size={12} /> Dépublier</> : <><Eye size={12} /> Publier</>}
                    </button>
                    <button type="button" onClick={() => startEdit(it)} className="border border-black hover:bg-black hover:text-[var(--paper)] px-3 py-1.5 label-mono text-xs flex items-center gap-1" data-testid={`cms-edit-${it.id}`}>
                      <Pencil size={12} /> Modifier
                    </button>
                    <button type="button" onClick={() => remove(it.id)} className="border border-[var(--signal)] text-[var(--signal)] hover:bg-[var(--signal)] hover:text-[var(--paper)] px-3 py-1.5 label-mono text-xs flex items-center gap-1" data-testid={`cms-delete-${it.id}`}>
                      <Trash size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
